import Modal from '@components/Modal'
import { formatTimeOfDay } from '@lib/datetime'
import { Event } from '@types'
import React from 'react'
import styled from 'styled-components'
import { black, blue, textMuted } from '@/theme/colors'
import { fontSizes, text, leading } from '@/theme/typography'
import { EmptyNote } from '@components/EmptyState'

interface ViewEventModalProps {
	isOpen: boolean
	onClose: () => void
	event: Event
}

const Content = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`

const Label = styled.div`
	${text.eyebrow}
	color: ${textMuted};
`

const Value = styled.div`
	font-size: ${fontSizes.base};
	color: ${black};
	line-height: ${leading.body};
	white-space: pre-wrap;
	word-break: break-word;
`

const LinkList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
`

const LinkItem = styled.a`
	color: ${blue};
	text-decoration: none;
	word-break: break-all;

	&:hover {
		text-decoration: underline;
	}
`

const ViewEventModal: React.FC<ViewEventModalProps> = ({ isOpen, onClose, event }) => {
	const hasDetails =
		event.startTime ||
		event.description ||
		event.location?.primaryText ||
		(event.tripEventLinks && event.tripEventLinks.length > 0)

	return (
		<Modal isOpen={isOpen} onClose={onClose} title={event.name} closeOnBackdropClick={true}>
			<Content>
				{!hasDetails && (
					<EmptyNote>There is no other information on this event</EmptyNote>
				)}

				{event.startTime && (
					<div>
						<Label>Time</Label>
						<Value>{formatTimeOfDay(event.startTime)}</Value>
					</div>
				)}

				{event.description && (
					<div>
						<Label>Description</Label>
						<Value>{event.description}</Value>
					</div>
				)}

				{event.location?.primaryText && (
					<div>
						<Label>Location</Label>
						<Value>{event.location.primaryText}</Value>
					</div>
				)}

				{event.tripEventLinks && event.tripEventLinks.length > 0 && (
					<div>
						<Label>Links</Label>
						<LinkList>
							{event.tripEventLinks.map(link => {
								const hasProtocol =
									link.url.startsWith('http://') || link.url.startsWith('https://')
								const looksLikeUrl = link.url.includes('.') && !link.url.includes(' ')
								const hrefUrl =
									!hasProtocol && looksLikeUrl ? `https://${link.url}` : link.url
								const isClickable = hasProtocol || looksLikeUrl

								return isClickable ? (
									<LinkItem
										key={link.id}
										href={hrefUrl}
										target="_blank"
										rel="noopener noreferrer"
									>
										{link.url}
									</LinkItem>
								) : (
									<Value key={link.id}>{link.url}</Value>
								)
							})}
						</LinkList>
					</div>
				)}
			</Content>
		</Modal>
	)
}

export default ViewEventModal
